import { useEffect, useRef, useState } from 'react';
import { ClipboardList, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';
import { formatMeasure, type ExamReport, type IndicatorSeries } from '@/lib/assessment/examTrends';
import { loadExamReport } from '@/lib/firebase/queries/examReport';
import { IndicatorBadge } from '@/components/health/IndicatorBadge';
import { ChangeMarker } from '@/components/health/ChangeMarker';
import { ExamTrendChart } from '@/components/health/ExamTrendChart';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  /** Shown under the title when staff open another person's report. */
  customerName?: string;
  /** Already loaded (the beneficiary's own card); otherwise loaded on open. */
  report?: ExamReport | null;
  /** Indicator to open on. */
  initialKey?: string;
}

const valueText = (s: IndicatorSeries) =>
  typeof s.latest.value === 'number' ? formatMeasure(s.latest.value) : s.latest.value;

/**
 * «تقرير الفحص» (#112): every completed exam of a beneficiary over time. Chips
 * pick an indicator; its latest value, level, change and chart sit on top.
 * Below, a card per indicator with a sparkline (click to enlarge) and the list
 * of all exams. Opened by the beneficiary from «القياسات الصحية» and by staff
 * from the examinee table and the patient file.
 */
export function ExamReportDialog({ open, onOpenChange, customerId, customerName, report: given, initialKey }: Props) {
  const { t, dir, language } = useLanguage();
  const [loaded, setLoaded] = useState<ExamReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [selected, setSelected] = useState<string | null>(initialKey ?? null);
  const reqId = useRef(0);
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || given || !customerId) return;
    const my = ++reqId.current;
    setLoading(true);
    setFailed(false);
    loadExamReport(customerId)
      .then((r) => my === reqId.current && setLoaded(r))
      .catch((e) => {
        console.error('[ExamReportDialog] load failed', e);
        if (my === reqId.current) setFailed(true);
      })
      .finally(() => my === reqId.current && setLoading(false));
  }, [open, given, customerId]);

  useEffect(() => {
    if (open) setSelected(initialKey ?? null);
  }, [open, initialKey]);

  const report = given ?? loaded;
  const series = report?.series ?? [];
  const current = series.find((s) => s.key === selected) ?? series[0] ?? null;
  const fmtDate = (d: Date) => d.toLocaleDateString(language === 'ar' ? 'ar-SA-u-ca-gregory' : 'en-GB');
  const name = (key: string) => t(`common.health.indicators.${key}`);
  const lineNames = (s: IndicatorSeries) =>
    s.key === 'bloodPressure' ? [t('common.health.report.systolic'), t('common.health.report.diastolic')] : [name(s.key)];

  const examCount = report?.exams.length ?? 0;
  const lastExam = examCount ? report!.exams[examCount - 1] : null;
  const summary = lastExam
    ? t(examCount === 1 ? 'common.health.report.summaryOne' : 'common.health.report.summary', {
        count: examCount,
        date: fmtDate(lastExam.date),
      })
    : '';

  const pick = (key: string) => {
    setSelected(key);
    topRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir={dir} className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="exam-report-dialog">
        <DialogHeader className="text-start">
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-primary" aria-hidden />
            {t('common.health.report.title')}
          </DialogTitle>
          <DialogDescription data-testid="exam-report-summary">
            {[customerName, summary].filter(Boolean).join(' · ')}
          </DialogDescription>
        </DialogHeader>

        {loading && !report ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label={t('common.loading')} />
          </div>
        ) : failed && !report ? (
          <p className="text-center py-8 text-muted-foreground">{t('common.health.report.loadFailed')}</p>
        ) : !current ? (
          <p className="text-center py-8 text-muted-foreground">{t('common.health.report.noExams')}</p>
        ) : (
          <div className="space-y-5" ref={topRef}>
            <div role="tablist" aria-label={t('common.health.report.indicators')} className="flex flex-wrap gap-2">
              {series.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  role="tab"
                  aria-selected={s.key === current.key}
                  data-testid={`exam-report-chip-${s.key}`}
                  onClick={() => setSelected(s.key)}
                  className={cn(
                    'rounded-full border px-3 py-1 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    s.key === current.key ? 'bg-[#004250] text-white border-[#004250]' : 'bg-background hover:bg-muted'
                  )}
                >
                  {name(s.key)}
                </button>
              ))}
            </div>

            <section role="tabpanel" aria-label={name(current.key)} className="rounded-lg border p-4 space-y-3" data-testid="exam-report-detail">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-semibold">
                    {name(current.key)} <span className="text-xs text-muted-foreground font-normal">({current.unit})</span>
                  </div>
                  <div className="text-xs text-muted-foreground">{t('common.health.report.latest')}</div>
                  <div className="text-2xl font-bold" dir="ltr" style={{ textAlign: dir === 'rtl' ? 'right' : 'left' }}>
                    {valueText(current)} <span className="text-sm font-normal text-muted-foreground">{current.unit}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <IndicatorBadge reading={current.latest} t={t} />
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <ChangeMarker change={current.change} t={t} />
                    {current.change.kind === 'changed' && <span>{t('common.health.report.sincePrevious')}</span>}
                  </div>
                </div>
              </div>
              <div role="img" aria-label={t('common.health.report.chartLabel', { name: name(current.key) })} dir="ltr">
                <ExamTrendChart series={current} formatDate={fmtDate} lineNames={lineNames(current)} />
              </div>
              <Legend t={t} bp={current.key === 'bloodPressure'} names={lineNames(current)} hasBands={!!current.bands} />
            </section>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {series.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => pick(s.key)}
                  aria-label={`${name(s.key)}: ${valueText(s)} ${s.unit}`}
                  aria-pressed={s.key === current.key}
                  data-testid={`exam-report-card-${s.key}`}
                  className={cn(
                    'rounded-lg border p-3 text-start transition-colors hover:bg-muted/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    s.key === current.key && 'border-[#004250]'
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium">{name(s.key)}</span>
                    <IndicatorBadge reading={s.latest} t={t} />
                  </div>
                  <div className="flex items-baseline justify-between gap-2 mt-1">
                    <span dir="ltr" className="text-lg font-semibold">
                      {valueText(s)} <span className="text-xs font-normal text-muted-foreground">{s.unit}</span>
                    </span>
                    <ChangeMarker change={s.change} t={t} />
                  </div>
                  <div dir="ltr" aria-hidden>
                    <ExamTrendChart series={s} sparkline formatDate={fmtDate} />
                  </div>
                </button>
              ))}
            </div>

            <section aria-labelledby="exam-report-all">
              <h3 id="exam-report-all" className="font-semibold mb-2">
                {t('common.health.report.allExams')}
              </h3>
              <ul className="divide-y rounded-lg border" data-testid="exam-report-exams">
                {[...(report?.exams ?? [])].reverse().map((e, i) => (
                  <li key={e.id ?? i} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span>{fmtDate(e.date)}</span>
                    <span className="text-muted-foreground">{t('common.health.report.clinicalExam')}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Legend({ t, bp, names, hasBands }: { t: (k: string) => string; bp: boolean; names: string[]; hasBands: boolean }) {
  return (
    <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
      {hasBands &&
        (['good', 'acceptable', 'bad'] as const).map((level) => (
          <span key={level} className="inline-flex items-center gap-1">
            <span
              className={cn(
                'inline-block h-2.5 w-2.5 rounded-sm',
                level === 'good' ? 'bg-green-500/40' : level === 'acceptable' ? 'bg-yellow-500/40' : 'bg-red-500/40'
              )}
            />
            {t(`common.health.level.${level}`)}
          </span>
        ))}
      {bp &&
        names.map((n, i) => (
          <span key={n} className="inline-flex items-center gap-1">
            <span className="inline-block h-0.5 w-4" style={{ background: i === 0 ? '#004250' : '#7c3aed' }} />
            {n}
          </span>
        ))}
    </div>
  );
}
